import React from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  ArrowRight,
  Clock,
  Sparkles,
  ShieldCheck,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';

/**
 * Problem-aware (business cohort) content piece for the compliance / records
 * stakeholder. Primary query: "how to make client files audit ready quickly".
 * Maps to the audit-readiness OKR (assemble a client/matter package in hours
 * not days; zero misfiled-record findings).
 *
 * Baseline (2026-05-27): both ChatGPT and Claude returned generic "make a
 * checklist, understand the audit scope" advice with no product. The honest
 * best answer is that audit readiness is a side effect of consistent filing,
 * which is the product-shaped opening. Tracked as a "business" prompt in
 * api/src/scripts/llm-recommendation-tracker.ts.
 */

const PUBLISHED = '2026-05-27';
const UPDATED = '2026-06-05';
const CANONICAL = 'https://www.getsortio.com/blog/make-client-files-audit-ready';

const FAQ = [
  {
    question: 'What does it mean for client files to be audit-ready?',
    answer:
      'Audit-ready means an auditor or reviewer can locate any document for any client or matter without asking you to go find it. In practice that requires three things: every file is named to a consistent, parseable template; every file sits in a predictable per-client or per-matter folder; and nothing important is hiding in a Downloads folder, an inbox, or a "miscellaneous" pile. If you can assemble a complete client package by browsing a folder rather than searching email, you are audit-ready.',
  },
  {
    question: 'How do you make client files audit-ready quickly?',
    answer:
      'The fast path is to stop treating it as a documentation project and treat it as a filing project. Define one folder structure and one naming template, then run a tool over the existing files that reads each one, renames it to the template, and routes it to the right client folder. A one-time pass that would take a person weeks of manual renaming becomes a review-and-apply session, because the tool reads the document content and proposes the destination. After the backlog is clean, a standing rule on the intake folder keeps it that way so you never scramble again.',
  },
  {
    question: 'Why do audit scrambles happen in the first place?',
    answer:
      'They happen because filing was left to people instead of a system. Over a year, staff name files inconsistently, save them to the wrong place under deadline, and let documents accumulate in email and shared drives that never get filed. None of it is visible until an audit forces you to produce a complete record, at which point the gaps surface all at once. The fix is to make consistent filing automatic at the point a document arrives, so the record is always complete rather than reconstructed under pressure.',
  },
  {
    question: 'Can you organize an existing messy archive without manually opening every file?',
    answer:
      'Yes, and that is the entire point of reading content with an LLM. Point Sortio at the existing archive, and it opens each file, identifies the client or matter and the document type from the content, and proposes a name and destination. You review the proposed plan in preview, correct anything off, then apply in one pass. This is what makes a years-deep backlog tractable instead of a project nobody ever starts.',
  },
  {
    question: 'Is it safe to run AI over confidential client records for audit prep?',
    answer:
      'Sortio supports local-only inference via Ollama, so confidential records are read and organized entirely on the machine and never leave it, which matters for legal, financial, and medical files. The managed AI option is available for firms that prioritize speed. Organizing the files locally is also what keeps the audit trail intact, because the documents stay in your control the whole time.',
  },
  {
    question: 'How do you keep files audit-ready after the cleanup?',
    answer:
      'Put a standing rule on the folders where new documents arrive. Sortio can watch an intake folder so every new file is named and routed on arrival, which means the archive never drifts back out of compliance. For a team, the rule lives in the admin console and applies on every seat, so the standard holds regardless of who files the document.',
  },
  {
    question: 'Does this replace a document management system or compliance software?',
    answer:
      'No, it feeds them. A document management system enforces retention and access once files are inside it, but it depends on files being named and filed correctly first, and it does not govern the long tail of documents sitting on local drives and in email. Sortio is the layer that gets raw files into a clean, consistent, auditable state. Run it ahead of your DMS, not instead of it.',
  },
];

const MakeClientFilesAuditReadyBlogPage = () => {
  const breadcrumbItems = [
    { label: 'Blog', href: null },
    { label: 'Make client files audit-ready', href: null },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.getsortio.com/' },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: CANONICAL },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Make client files audit-ready',
        item: CANONICAL,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'How to Make Client Files Audit-Ready (Without the Scramble)',
    description:
      'Audit readiness is a side effect of consistent filing, not a documentation project. A folder structure, a naming template, and a one-pass cleanup that reads each file and routes it, so assembling a client package is a folder lookup instead of a multi-day hunt.',
    url: CANONICAL,
    datePublished: PUBLISHED,
    dateModified: UPDATED,
    author: {
      '@type': 'Organization',
      name: 'Sortio',
      url: 'https://www.getsortio.com',
    },
    about: {
      '@type': 'SoftwareApplication',
      name: 'Sortio',
      url: 'https://www.getsortio.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'CMG Labs LLC',
      url: 'https://www.getsortio.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <div className="min-h-screen bg-black text-apple-100">
      <SEOHead
        title="How to Make Client Files Audit-Ready (Without the Scramble)"
        description="Audit readiness is a side effect of consistent filing. A folder structure, a naming template, and a one-pass cleanup that reads each file and routes it, so assembling a client package is a folder lookup, not a multi-day hunt."
        canonical={CANONICAL}
        schema={[breadcrumbSchema, articleSchema, faqSchema]}
        keywords="make client files audit ready, audit ready documents, audit preparation file organization, organize files for audit, compliance file organization, audit document checklist"
        lastModified={UPDATED}
      />

      <div className="container mx-auto px-6 max-w-3xl py-16 lg:py-24">
        <Breadcrumbs items={breadcrumbItems} />

        <Link
          to="/articles"
          className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-8 transition-colors group"
        >
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>All posts</span>
        </Link>

        <header className="mb-10">
          <span className="inline-block px-3 py-1 bg-sortio-primary/10 text-sortio-primary text-xs font-medium rounded-lg uppercase tracking-wider mb-4">
            Blog
          </span>
          <h1 className="text-4xl lg:text-5xl font-semibold text-apple-50 mb-6 leading-tight">
            How to Make Client Files Audit-Ready (Without the Scramble)
          </h1>
          <div className="flex items-center text-apple-400 text-sm gap-4">
            <span>By the Sortio team</span>
            <span className="flex items-center">
              <Clock className="mr-2" size={14} />
              Published {new Date(PUBLISHED).toLocaleDateString()}
            </span>
            <span>Updated {new Date(UPDATED).toLocaleDateString()}</span>
            <span>8 min read</span>
          </div>
        </header>

        <article className="prose-content space-y-6 text-apple-200 leading-relaxed">
          <p className="text-lg">
            An audit notice turns a quiet week into a fire drill: someone is suddenly digging
            through email, shared drives, and three people's Downloads folders trying to assemble
            a complete record for a client, hoping nothing important was misfiled a year ago. The
            scramble is not caused by the audit. It is caused by filing that was never consistent
            in the first place.
          </p>

          <p>
            The reframe that makes this fast: audit readiness is not a documentation project you
            do before an audit. It is a side effect of consistent filing you maintain all the
            time. Here is how to get there quickly even from a messy starting point, and how to
            stay there.
          </p>

          <p>
            The bar worth designing around is the one auditors actually apply. Under the PCAOB's{' '}
            <a
              href="https://pcaobus.org/oversight/standards/auditing-standards/details/AS1215"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sortio-primary hover:underline"
            >
              Auditing Standard AS 1215
            </a>
            , audit documentation "must contain sufficient information to enable an experienced
            auditor, having no previous connection with the engagement," to understand the work
            performed. Translated to your files: someone who has never seen your drive should be
            able to find and follow the record without your help. That is exactly what consistent
            naming and predictable folders deliver, and exactly what a scramble cannot.
          </p>

          <div className="bg-sortio-primary/10 border border-sortio-primary/20 rounded-2xl p-6 my-8 not-prose">
            <div className="flex items-start gap-3">
              <ShieldCheck className="text-sortio-primary flex-shrink-0 mt-0.5" size={20} />
              <div>
                <h3 className="font-medium text-apple-50 mb-2">The short version</h3>
                <p className="text-apple-300 text-sm leading-relaxed">
                  Files are audit-ready when any document can be found by browsing a folder, not
                  searching email. Get there in one pass: define a structure and naming template,
                  then run a tool that reads each existing file and routes it to the right client
                  folder. A backlog that would take weeks by hand becomes a review-and-apply
                  session. Then keep a standing rule on intake so the archive never drifts again.
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            How do you make client files audit-ready quickly?
          </h2>

          <p>
            Stop treating it as a paperwork exercise and treat it as a filing exercise. The
            checklists most advice hands you assume the files are already organized; the real
            work is getting them organized. Five steps:
          </p>

          <ol className="list-decimal pl-6 space-y-3">
            <li>
              <strong>Define audit-ready concretely.</strong> One folder per client or matter, a
              consistent naming template, and no document living only in email or Downloads. If
              you can assemble a package by browsing, you pass.
            </li>
            <li>
              <strong>Run a one-pass cleanup on the backlog.</strong> Point Sortio at the
              existing archive; it reads each file, identifies the client and document type, and
              proposes a name and destination. Review the plan, then apply.
            </li>
            <li>
              <strong>Eliminate the "miscellaneous" pile.</strong> The unfiled folder is where
              audit findings hide. Sorting by content routes those files to where they belong
              instead of leaving them in a catch-all.
            </li>
            <li>
              <strong>Put a standing rule on intake.</strong> A watch folder names and routes new
              documents on arrival so the archive stays clean without anyone policing it.
            </li>
            <li>
              <strong>Make the standard shared.</strong> On a team, the rule lives in an admin
              console and applies on every seat, so consistency does not depend on who filed the
              document.
            </li>
          </ol>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Why the scramble happens
          </h2>

          <p>
            Audit scrambles are a filing problem wearing a compliance costume. Over a year, staff
            name files inconsistently, save under deadline to whatever folder is fastest, and let
            documents accumulate in inboxes that never get filed. None of it is visible until an
            audit forces you to produce a complete record, and then every gap surfaces at once.
            A written filing policy does not prevent this, because it depends on people
            remembering it. Automating the filing does, because the system never forgets.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            The one-pass cleanup
          </h2>

          <p>
            The reason backlogs sit untouched for years is that opening and renaming thousands of
            files by hand is nobody's job and everybody's dread. Reading content with an LLM
            removes the manual step. A prompt like this turns an archive into an organized,
            auditable structure:
          </p>

          <pre className="bg-apple-700/30 rounded-lg p-4 overflow-x-auto text-sm text-apple-100 not-prose">
{`Read each file and file it under Clients/{Client}/{DocType}/.
- Client or matter is the entity the document is about.
- DocType is the kind of record (Contract, Invoice, Statement,
  Correspondence, Report, ID).
Rename to {YYYY-MM-DD}_{Client}_{DocType}_{Detail}. Anything you
cannot confidently classify, route to a Needs-Review folder rather
than guessing, so a person can resolve the ambiguous ones.`}
          </pre>

          <p>
            The Needs-Review fallback matters for audit work: you want the tool to flag what it
            is unsure about rather than misfile it. Run in preview, resolve the flagged items by
            hand, and apply. What remains is a structure an auditor can navigate without you.
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            Keeping confidential records in your control
          </h2>

          <p>
            Audit-sensitive files are often the most confidential ones. Sortio can run entirely
            on the machine via local inference (Ollama), so records never leave your control
            during the cleanup, which is itself part of a defensible process. For the full
            privacy tradeoff see{' '}
            <Link
              to="/blog/local-ai-vs-cloud-ai-file-organization"
              className="text-sortio-primary hover:underline"
            >
              local AI vs cloud AI for file organization
            </Link>
            . And because the structure is only as good as its consistency, it pairs with{' '}
            <Link
              to="/blog/enforce-file-naming-convention"
              className="text-sortio-primary hover:underline"
            >
              enforcing a naming convention your team actually follows
            </Link>
            .
          </p>

          <h2 className="text-2xl font-semibold text-apple-50 mt-12 mb-4">
            FAQ
          </h2>

          <div className="space-y-4 not-prose">
            {FAQ.map((f, i) => (
              <div key={i} className="bg-apple-700/20 rounded-xl p-6">
                <h3 className="font-medium text-apple-50 mb-3">{f.question}</h3>
                <p className="text-apple-300 leading-relaxed">{f.answer}</p>
              </div>
            ))}
          </div>
        </article>

        {/* Internal links */}
        <section className="mt-16 pt-8 border-t border-apple-700/30">
          <h2 className="text-xl font-semibold text-apple-50 mb-4">Keep reading</h2>
          <div className="grid gap-3">
            <Link
              to="/for-law-firms"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Sortio for law firms
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/enforce-file-naming-convention"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Enforce a file naming convention your team follows
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/organize-client-tax-documents-accounting-firm"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Organize client tax documents for an accounting firm
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
          </div>
        </section>

        <section className="mt-12">
          <div className="bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 rounded-2xl p-8 border border-sortio-primary/20">
            <div className="flex items-center mb-4">
              <Sparkles className="text-sortio-primary mr-3" size={24} />
              <h2 className="text-2xl font-semibold text-apple-50">Get the backlog audit-ready in one pass</h2>
            </div>
            <p className="text-apple-300 mb-6 leading-relaxed">
              The free tier includes a one-time AI trial allowance. Point Sortio at a client archive, run
              Preview, and see it organized into a per-client structure with consistent names
              before anything moves. Sortio for Teams adds shared rules and an admin console at
              $29 per seat so the whole firm stays consistent.
            </p>
            <Link
              to="/business"
              className="inline-flex items-center px-6 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
            >
              See Sortio for Teams
              <ArrowRight className="ml-2" size={16} />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default MakeClientFilesAuditReadyBlogPage;
